import { useCallback, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import Swal from 'sweetalert2';

import { useLanguage } from '@languages/index';
import API from 'api';
import { DeleteProductRequestParams, ProductDto } from 'api/repositories/product/product.types';
import { PRODUCT_DTO_ENUMS } from 'api/repositories/product/product.constants';
import Button from 'components/button';
import CampaignFormField from '../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormFieldWrapperProps } from '../formFields.types';
import CreateProductMasterModal from 'components/createProductMasterModal/createProductMasterModal';
import { RENTAL_TYPE } from 'components/createProductMasterModal/createProductMasterModal';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';

const ProductsField = ({ elementIndex }: CampaignFormFieldWrapperProps) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const { modal, auth, brand, products, refetchProducts, temporaryKey, updateCampaignId } =
    useContext(CampaignFormContext);

  const { mutate: deleteProductMutate } = useMutation({
    mutationFn: (params: DeleteProductRequestParams) => API.Product.delete(params),
    onSuccess: () => {
      modal.showModal({ text: languageTexts.create_delete_modal_success });
      refetchProducts();
    },
    onError: (error: any) => {
      modal.showModal({ text: error?.message });
    },
  });

  const onClickAddProduct = useCallback(() => {
    modal.showModal({
      isVisible: true,
      preventCloseWithBackgroundDimmer: true,
      content: () => {
        return (
          <CreateProductMasterModal
            rentalType={RENTAL_TYPE.SEEDING}
            temporaryKey={temporaryKey}
            onSuccess={refetchProducts}
          />
        );
      },
    });
  }, [temporaryKey]);

  const onClickEditProduct = useCallback(
    (productMasterId: ProductDto['productMasterId']) => {
      API.Product.index({
        type: PRODUCT_DTO_ENUMS.TYPE.PRODUCT_MASTER,
        brandToken: auth.token,
        id: productMasterId,
      }).then((response) => {
        const master = response.data.products[0];

        modal.showModal({
          isVisible: true,
          preventCloseWithBackgroundDimmer: true,
          content: () => {
            return (
              <CreateProductMasterModal
                method={'EDIT'}
                master={master}
                rentalType={RENTAL_TYPE.SEEDING}
                temporaryKey={temporaryKey}
                onSuccess={refetchProducts}
              />
            );
          },
        });
      });
    },
    [temporaryKey],
  );

  const onClickDeleteProduct = useCallback((productId: ProductDto['id']) => {
    Swal.fire({
      icon: 'info',
      text: languageTexts.campaign_form_delete_product_confirm,
      showCancelButton: true,
      confirmButtonText: languageTexts.delete,
      cancelButtonText: languageTexts.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteProductMutate({
          type: PRODUCT_DTO_ENUMS.TYPE.PRODUCT,
          id: productId,
          brandToken: auth.token,
        });
      }
    });
  }, []);

  return (
    <CampaignFormField
      label={`${elementIndex}. ${languageTexts.campaign_form_products_field_label}`}
      description={
        updateCampaignId
          ? `${languageTexts.campaign_form_cant_be_update_modal_title} ${languageTexts.campaign_form_cant_be_update_modal_text}`
          : languageTexts.campaign_form_products_field_desc(brand.isCategoryFashion)
      }
    >
      {/* 제품 목록 */}
      <div className="flex flex-col gap-3">
        {products?.length > 0 &&
          products.map((product, index) => {
            return (
              <ul key={`product-${index}`}>
                <li className="relative flex flex-col p-[10px] border border-gray-200">
                  {/* 제품 삭제 버튼 */}
                  {!updateCampaignId && (
                    <button className="absolute top-[15px] right-[15px]" type="button">
                      <img
                        className="w-[22px] h-[22px]"
                        src="/assets/icons/ic-close-black-squared.svg"
                        onClick={() => onClickDeleteProduct(product.id)}
                      />
                    </button>
                  )}

                  {/* 제품 정보 */}
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col">
                      <img className="w-[80px] h-[80px]" src={product.thumbnailSmUrl} />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="flex flex-row truncate max-w-[235px]">
                        <span className="text-sm tracking-tighter">{product.name}</span>
                      </div>
                      <div className="flex flex-row truncate max-w-[235px]">
                        <span className="text-sm tracking-tighter">{product.colorName}</span>
                        {brand.isCategoryFashion && (
                          <>
                            <span className="text-sm tracking-tighter">&nbsp;/&nbsp;</span>
                            <span className="text-sm tracking-tighter">{product.sizeName}</span>
                          </>
                        )}
                        <span className="text-sm tracking-tighter">&nbsp;/&nbsp;</span>
                        <span className="text-sm tracking-tighter">{languageTexts._product_qty(product.qty)}</span>
                      </div>

                      {/* 제품 수정 버튼 */}
                      {!updateCampaignId && (
                        <div className="flex flex-row justify-end">
                          <span
                            className="text-sm tracking-tighter text-[#999999] hover:text-black cursor-pointer underline"
                            onClick={() => onClickEditProduct(product.productMasterId)}
                          >
                            {languageTexts.modify2}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              </ul>
            );
          })}
      </div>

      {/* 제품 추가 버튼 */}
      {!updateCampaignId && (
        <div className="mt-4">
          <Button
            className="px-4 py-3 text-center justify-center"
            bordered
            borderColor="black"
            text={
              products?.length > 0
                ? languageTexts.campaign_form_products_field_add_btn
                : languageTexts.campaign_form_products_field_input_btn
            }
            onClick={onClickAddProduct}
          />
        </div>
      )}
    </CampaignFormField>
  );
};

export default ProductsField;
